import React from 'react';
import { Box, Button, Loader, Modal, Table, Text, TextInput } from '@mantine/core';
import { Company, useCompanies } from '../hooks/useCompanies';
import {
  CreateCompanyInput,
  useCreateCompanyMutation,
} from 'src/secret-menu/hooks/useCreateCompanyMutation';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

export default function CompaniesTab() {
  const { companies, isLoading, isError } = useCompanies();
  const [opened, { open, close }] = useDisclosure(false);
  const { createCompany, isLoading: isCreating } = useCreateCompanyMutation();

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      fca_firm_reference_number: '',
    },
    validate: {
      name: value =>
        value.length > 50
          ? 'Name must be at most 50 characters'
          : !/[A-Za-z0-9]/.test(value)
            ? 'Name must contain at least one alphanumeric character'
            : null,
    },
  });

  if (isLoading) {
    return <Text>Loading...</Text>;
  }

  if (isError) {
    return <Text>Failed to load companies</Text>;
  }

  const handleCreateCompany = async (values: CreateCompanyInput) => {
    try {
      await createCompany({ ...values });
      form.reset();
      close();
    } catch (error) {
      notifyAndLogError(
        'Failed to create company',
        'An error occurred while creating the company.',
        error,
      );
    }
  };

  const rows = companies?.map((company: Company) => (
    <Table.Tr key={company.name}>
      <Table.Td>{company.name}</Table.Td>
      <Table.Td>{company.tenantName}</Table.Td>
      <Table.Td>{company.fcaFirmReferenceNumbers?.map(n => n.number).join('  |  ')}</Table.Td>
      <Table.Td>{company.clientsCount}</Table.Td>
    </Table.Tr>
  ));

  return (
    <Box>
      <Modal onClose={close} opened={opened} title="Create company">
        {isCreating ? (
          <Loader id="loader" />
        ) : (
          <form onSubmit={form.onSubmit(values => handleCreateCompany(values))}>
            <TextInput
              required
              label="Company name"
              placeholder="eg. Afternoon limited"
              key={form.key('name')}
              {...form.getInputProps('name')}
            />
            <TextInput
              required
              label="FCA Firm Reference Number"
              placeholder="eg. 1234567"
              key={form.key('fca_firm_reference_number')}
              {...form.getInputProps('fca_firm_reference_number')}
            />
            <Button mt={'md'} type="submit">
              Submit
            </Button>
          </form>
        )}
      </Modal>

      <Button onClick={open}>Create</Button>
      <Table mt={'md'}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            <Table.Th>Tenant name</Table.Th>
            <Table.Th>FCA firm reference number</Table.Th>
            <Table.Th>Clients count</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>{rows}</Table.Tbody>
      </Table>
    </Box>
  );
}
